import React, {useEffect, useMemo, useState} from 'react';
import {
    CButton,
    CInput,
    CModal,
    CModalBody,
    CModalHeader,
    CModalTitle,
    CNav,
    CNavItem,
    CNavLink,
    CSpinner,
    CTabContent,
    CTabPane,
    CTabs
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {normalizarBusca} from "./executionResults";

export interface ResultLog {
    messages: Array<string>,
    mockups: Array<any>,
    testCase: Array<any>,
    documento: any,
    content?: string | null
}

interface Props {
    aberto: boolean,
    carregando: boolean,
    titulo: string,
    log: ResultLog | null,
    onFechar: () => void
}

const classeLinha = (mensagem: string) => {
    if (/\[OK]/.test(mensagem)) return 'ok';
    if (/\[ERRO]|Detalhe:|Payload inválido/i.test(mensagem)) return 'erro';
    if (/interrompid|cancelad|encerrada sem|tempo limite|Executor indispon/i.test(mensagem)) return 'aviso';
    if (/ - (INICIO|FIM) /.test(mensagem)) return 'marco';
    return '';
}

const copiar = (texto: string) => {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(texto).catch(() => {});
    }
}

const ResultLogModal = ({aberto, carregando, titulo, log, onFechar}: Props) => {
    const [busca, setBusca] = useState<string>('');

    useEffect(() => {
        if (!aberto) {
            setBusca('');
        }
    }, [aberto]);

    const mensagens = useMemo(() => log?.messages || [], [log]);
    const filtradas = useMemo(() => {
        const termo = normalizarBusca(busca);
        return termo ? mensagens.filter(m => normalizarBusca(m).includes(termo)) : mensagens;
    }, [mensagens, busca]);

    return (
        <CModal show={aberto} onClose={onFechar} size="lg" scrollable>
            <CModalHeader closeButton>
                <CModalTitle className="text-truncate">{titulo}</CModalTitle>
            </CModalHeader>
            <CModalBody>
                {carregando && (
                    <div className="text-center p-4"><CSpinner color="primary"/></div>
                )}
                {!carregando && log && (
                    <CTabs>
                        <CNav variant="tabs">
                            <CNavItem><CNavLink>Registro ({mensagens.length})</CNavLink></CNavItem>
                            <CNavItem><CNavLink>Mockup</CNavLink></CNavItem>
                            <CNavItem><CNavLink>Caso de teste</CNavLink></CNavItem>
                            {log.documento && <CNavItem><CNavLink>Documento IA</CNavLink></CNavItem>}
                        </CNav>
                        <CTabContent className="pt-3">
                            <CTabPane>
                                <div className="d-flex flex-wrap mb-2" style={{gap: '.5rem'}}>
                                    <CInput
                                        type="search"
                                        value={busca}
                                        onChange={(e: any) => setBusca(e.target.value)}
                                        placeholder="Filtrar mensagens"
                                        style={{flex: '1 1 220px'}}
                                    />
                                    <CButton color="secondary" variant="outline" onClick={() => copiar(mensagens.join('\n'))}
                                             title="Copiar o registro completo" disabled={mensagens.length === 0}>
                                        <CIcon name="cil-copy" className="mr-1"/>Copiar
                                    </CButton>
                                </div>
                                {mensagens.length === 0
                                    ? <div className="tester-vazio">Nenhuma mensagem registrada ainda.</div>
                                    : (
                                        <div className="tester-log">
                                            {filtradas.map((mensagem, index) => (
                                                <div key={index} className={'tester-log-linha ' + classeLinha(mensagem)}>{mensagem}</div>
                                            ))}
                                            {filtradas.length === 0 && <div className="tester-log-linha">Nenhuma mensagem com esse texto.</div>}
                                        </div>
                                    )}
                            </CTabPane>
                            <CTabPane>
                                {(log.mockups || []).filter(Boolean).map((mockup: any, index: number) => (
                                    <div key={index} className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <strong>{mockup?.name || 'Mockup'}</strong>
                                            <CButton size="sm" color="secondary" variant="ghost" onClick={() => copiar(JSON.stringify(mockup?.mockup ?? mockup, null, 2))}>
                                                <CIcon name="cil-copy" className="mr-1"/>Copiar JSON
                                            </CButton>
                                        </div>
                                        <pre className="tester-json">{JSON.stringify(mockup?.mockup ?? mockup, null, 2)}</pre>
                                    </div>
                                ))}
                                {(log.mockups || []).filter(Boolean).length === 0 && <div className="tester-vazio">Sem mockup nesta execução.</div>}
                            </CTabPane>
                            <CTabPane>
                                {(log.testCase || []).filter(Boolean).map((testCase: any, index: number) => (
                                    <div key={index}>
                                        <dl className="tester-campo-leitura">
                                            <dt>ID</dt><dd>{testCase.id}</dd>
                                            <dt>Código</dt><dd>{testCase.codigo || '—'}</dd>
                                            <dt>Nome</dt><dd>{testCase.name}</dd>
                                            <dt>Tipo</dt><dd>{testCase.executor_type === 'script' ? 'Script Cypress' : 'Operações'}</dd>
                                        </dl>
                                        {testCase.executor_type === 'script'
                                            ? <pre className="tester-json">{testCase.script || '(script vazio)'}</pre>
                                            : (testCase.operations || []).map((operation: any, indexOperacao: number) => (
                                                <details key={indexOperacao} className="mb-2">
                                                    <summary>{indexOperacao + 1}. {operation.name}</summary>
                                                    <pre className="tester-json mt-1">{JSON.stringify(operation.params, null, 2)}</pre>
                                                </details>
                                            ))}
                                    </div>
                                ))}
                                {(log.testCase || []).filter(Boolean).length === 0 && <div className="tester-vazio">Sem caso de teste nesta execução.</div>}
                            </CTabPane>
                            {log.documento && (
                                <CTabPane>
                                    <div className="mb-2"><strong>{log.documento.nome}</strong></div>
                                    <a href={log.documento.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary mb-3">
                                        <CIcon name="cil-external-link" className="mr-1"/>Abrir documento
                                    </a>
                                    <div className="text-muted small">Path: {log.documento.path}</div>
                                </CTabPane>
                            )}
                        </CTabContent>
                    </CTabs>
                )}
            </CModalBody>
        </CModal>
    );
}

export default ResultLogModal;
